interface MarkdownMetadata {
  title?: string;
  lastUpdated?: string;
  version?: string;
  effectiveDate?: string;
  [key: string]: any;
}

interface ParsedMarkdown {
  content: string;
  metadata: MarkdownMetadata;
}

/**
 * Parses markdown content with frontmatter
 * @param markdown - Raw markdown string with optional frontmatter
 * @returns Parsed content and metadata
 */
export function parseMarkdownWithFrontmatter(markdown: string): ParsedMarkdown {
  // Check if the markdown starts with frontmatter
  if (!markdown.startsWith('---')) {
    return {
      content: markdown,
      metadata: {}
    };
  }

  // Find the end of frontmatter
  const endOfFrontmatter = markdown.indexOf('\n---\n', 4);
  if (endOfFrontmatter === -1) {
    return {
      content: markdown,
      metadata: {}
    };
  }

  // Extract frontmatter and content
  const frontmatterString = markdown.substring(4, endOfFrontmatter);
  const content = markdown.substring(endOfFrontmatter + 5); // +5 to skip \n---\n

  // Parse frontmatter
  const metadata: MarkdownMetadata = {};
  const lines = frontmatterString.split('\n');
  
  lines.forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex !== -1) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();
      
      // Remove quotes if present
      if ((value.startsWith('"') && value.endsWith('"')) || 
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }
      
      metadata[key] = value;
    }
  });

  return {
    content,
    metadata
  };
}

/**
 * Formats a date string for display
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string
 */
export function formatLegalDate(dateString: string | undefined): string {
  if (!dateString) return '';
  
  try {
    // Parse the date string
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    
    // Format as "5 June 2025"
    const formatter = new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    return formatter.format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original string if parsing fails
  }
}